import { immerable } from 'immer';
import { isContainerNode, isModuleNode, isRootNode, isSectionNode } from './utils';
import { isEmptyObject } from 'ContentUtils/helpers/ObjectHelpers';

/**
 * Parameters for creating a GridNode instance.
 * @template T - The specific GridNodeType for this node
 */

/**
 * Abstract base class representing a node in the grids layout system.
 *
 * GridNode provides the fundamental structure and behavior for all nodes
 * in a grid layout tree. Each node has an ID, type, value, and optional
 * parent/child relationships. Nodes can be roots, sections, containers,
 * or modules, each with specific behaviors and constraints.
 *
 * @template T - The specific GridNodeType for this node (defaults to GridNodeType)
 * @template V - The type of value stored in this node (defaults to unknown)
 */
export default class GridNode {
  /**
   * Creates a new GridNode instance.
   * @param gridNodeParams - Configuration object for the node
   */
  constructor(gridNodeParams) {
    this[immerable] = true;
    const {
      id,
      type,
      value,
      parentNodeId,
      childNodeIds,
      treeRef
    } = gridNodeParams;
    this._id = id;
    this._type = type;
    this._value = value;
    this._parentNodeId = parentNodeId;
    this._childNodeIds = childNodeIds;
    this._treeRef = treeRef;
  }

  /**
   * Creates a clone of this node with modified parameters.
   * This method must be implemented by concrete subclasses.
   * @param gridNodeParams - Partial parameters to change in the cloned node
   * @returns A new GridNode instance with the specified changes
   */

  /**
   * Serializes the node to a GridLayout object.
   * This method must be implemented by concrete subclasses.
   * @param options - Serialization options
   * @returns The serialized node
   */

  /**
   * Exports the node as an ExportedNode.
   * This method must be implemented by concrete subclasses.
   * @returns An ExportedNode
   */

  /**
   * Gets the label of this node.
   * @returns The node's label
   */

  /**
   * Gets the unique identifier of this node.
   * @returns The node's ID
   */
  getId() {
    return this._id;
  }

  /**
   * Gets the type of this node.
   * @returns The node's type
   */
  getType() {
    return this._type;
  }

  /**
   * Gets the value/data associated with this node.
   * @returns The node's value
   */
  getValue() {
    return this._value;
  }

  /**
   * Gets the ID of the root node in the tree containing this node.
   * @returns The root node's ID
   */
  getRootId() {
    return this.getTree().getRootId();
  }

  /**
   * Checks if this node is a root node.
   * @returns True if this is a root node, false otherwise
   */
  isRoot() {
    return isRootNode(this);
  }

  /**
   * Checks if this node is a section node.
   * @returns True if this is a section node, false otherwise
   */
  isSection() {
    return isSectionNode(this);
  }

  /**
   * Checks if this node is a container node.
   * @returns True if this is a container node, false otherwise
   */
  isContainer() {
    return isContainerNode(this);
  }

  /**
   * Checks if this node is a module node.
   * @returns True if this is a module node, false otherwise
   */
  isModule() {
    return isModuleNode(this);
  }

  /**
   * Checks if this node is a leaf node (has no children or is a module).
   * @returns True if this is a leaf node, false otherwise
   */
  isLeaf() {
    var _this$_childNodeIds;
    return this.isModule() || this.hasChildNodeIds() && ((_this$_childNodeIds = this._childNodeIds) === null || _this$_childNodeIds === void 0 ? void 0 : _this$_childNodeIds.length) === 0;
  }

  /**
   * Checks if this node has children
   * @returns True if this node has children, false otherwise
   */
  hasChildren() {
    var _this$_childNodeIds2;
    return this.hasChildNodeIds() && ((_this$_childNodeIds2 = this._childNodeIds) === null || _this$_childNodeIds2 === void 0 ? void 0 : _this$_childNodeIds2.length) > 0;
  }

  /**
   * Type guard to check if this node has a parent node.
   * Narrows the type to include a defined _parentNodeId property.
   * @returns True if the node has a parent, false otherwise
   */
  hasParentNode() {
    return this._parentNodeId !== undefined;
  }

  /**
   * Gets a reference to the tree containing this node.
   * @returns The BaseGridLayoutDataTree instance
   */
  getTree() {
    return this._treeRef;
  }

  /**
   * Gets the parent node of this node.
   * @returns The parent node instance
   * @throws {Error} When the node has no parent node
   */
  getParentNode() {
    if (this.hasParentNode()) {
      return this.getTree().findNode(this._parentNodeId);
    }
    throw new Error('Node has no parent node');
  }

  /**
   * Gets the ID of the parent node.
   * @returns The parent node's ID
   * @throws {Error} When the node has no parent node ID
   */
  getParentNodeId() {
    if (this.hasParentNode()) {
      return this._parentNodeId;
    }
    throw new Error('Node has no parent node id');
  }

  /**
   * Type guard to check if this node has child node IDs.
   * Narrows the type to include a defined _childNodeIds property.
   * @returns True if the node has child node IDs, false otherwise
   */
  hasChildNodeIds() {
    return this._childNodeIds !== undefined;
  }

  /**
   * Gets the array of child node IDs.
   * @returns Immutable array of child node IDs
   * @throws {Error} When the node has no child nodes
   */
  getChildNodeIds() {
    if (this.hasChildNodeIds()) {
      return this._childNodeIds;
    }
    throw new Error('Node has no child nodes');
  }

  /**
   * Gets the index of a specific child node by its ID.
   * @param childNodeId - The ID of the child node to find
   * @returns The index of the child node, or -1 if not found
   */
  indexOfChildNode(childNodeId) {
    return this.getChildNodeIds().indexOf(childNodeId);
  }

  /**
   * Checks if this node has styles. TODO: May need to rethink this since styles
   * are always present on the rpc type anyway. Should we check for breakpoint
   * styles instead?
   * @returns True if the node has styles, false otherwise
   */
  hasStyles() {
    const value = this.getValue();
    return !isEmptyObject(value.styles);
  }
  isGridContainerCell(breakpoint = 'default') {
    const parentNode = this.getParentNode();
    return parentNode.isContainer() && parentNode.isGrid(breakpoint);
  }

  /**
   * Normally, you'd never need to call this method. It's mainly used to sync
   * the tree ref of all the nodes in the tree after a tree has been cloned.
   * This is intentionally the only setter method for nodes, since we don't
   * want to allow for mutations of values on nodes.
   * @param treeRef - The new tree ref to set
   */
  _setTreeRef(treeRef) {
    this._treeRef = treeRef;
  }

  /**
   * Sets the child node IDs. You shouldn't need to call this method directly
   * unless it's for optimization purposes like during a batch update.
   * @param childNodeIds
   */
  _setChildNodeIds(childNodeIds) {
    this._childNodeIds = childNodeIds;
  }
}